<template>
  <div class="style-editor">
    <v-card flat>
      <v-card-title>
        <span class="text-h6">Layer Style anpassen</span>
      </v-card-title>

      <v-card-text>
        <!-- Geometry type specific style options -->
        <div v-if="styleType === 'point'">
          <!-- Show information if layer has a custom icon -->
          <v-alert v-if="hasCustomIcon" type="info" dense class="mb-3">
            Dieser Layer verwendet ein benutzerdefiniertes Icon. Nur die Größe
            kann angepasst werden.
          </v-alert>

          <!-- For custom icons, only show size control -->
          <div v-if="hasCustomIcon">
            <v-row>
              <v-col cols="12">
                <v-slider
                  v-model="localStyle.radius"
                  label="Icon Größe"
                  min="8"
                  max="64"
                  step="4"
                  thumb-label
                  hide-details
                ></v-slider>
              </v-col>
            </v-row>
          </div>

          <!-- For default markers, show all controls -->
          <div v-else>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="localStyle.color"
                  label="Füllfarbe"
                  readonly
                  @click="showFillColorPicker = !showFillColorPicker"
                  hide-details
                >
                  <template v-slot:prepend-inner>
                    <v-avatar
                      size="24"
                      :style="`background-color: ${localStyle.color}`"
                    ></v-avatar>
                  </template>
                </v-text-field>
                <v-menu
                  v-model="showFillColorPicker"
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{ props }">
                    <div v-bind="props"></div>
                  </template>
                  <v-card>
                    <v-color-picker
                      v-model="localStyle.color"
                      mode="hex"
                    ></v-color-picker>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn variant="text" @click="showFillColorPicker = false">Close</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="localStyle.outlineColor"
                  label="Umrandungsfarbe"
                  readonly
                  @click="showOutlineColorPicker = !showOutlineColorPicker"
                  hide-details
                >
                  <template v-slot:prepend-inner>
                    <v-avatar
                      size="24"
                      :style="`background-color: ${localStyle.outlineColor}`"
                    ></v-avatar>
                  </template>
                </v-text-field>
                <v-menu
                  v-model="showOutlineColorPicker"
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{ props }">
                    <div v-bind="props"></div>
                  </template>
                  <v-card>
                    <v-color-picker
                      v-model="localStyle.outlineColor"
                      mode="hex"
                    ></v-color-picker>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn variant="text" @click="showOutlineColorPicker = false">Close</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-menu>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="6">
                <v-slider
                  v-model="localStyle.radius"
                  label="Punktgröße"
                  min="2"
                  max="20"
                  step="1"
                  thumb-label
                  hide-details
                ></v-slider>
              </v-col>
              <v-col cols="12" sm="6">
                <v-slider
                  v-model="localStyle.weight"
                  label="Umrandungsdicke"
                  min="0"
                  max="5"
                  step="1"
                  thumb-label
                  hide-details
                ></v-slider>
              </v-col>
            </v-row>
          </div>
        </div>

        <div v-else-if="styleType === 'line'">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="localStyle.color"
                label="Linienfarbe"
                readonly
                @click="showLineColorPicker = !showLineColorPicker"
                hide-details
              >
                <template v-slot:prepend-inner>
                  <v-avatar
                    size="24"
                    :style="`background-color: ${localStyle.color}`"
                  ></v-avatar>
                </template>
              </v-text-field>
              <v-menu
                v-model="showLineColorPicker"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ props }">
                  <div v-bind="props"></div>
                </template>
                <v-card>
                  <v-color-picker
                    v-model="localStyle.color"
                    mode="hex"
                  ></v-color-picker>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn variant="text" @click="showLineColorPicker = false">Close</v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-slider
                v-model="localStyle.weight"
                label="Linienstärke"
                min="1"
                max="10"
                step="1"
                thumb-label
                hide-details
              ></v-slider>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-select
                v-model="localStyle.dashArray"
                label="Linienstil"
                :items="lineStyleOptions"
                dense
                hide-details
              ></v-select>
            </v-col>
          </v-row>
        </div>

        <div v-else-if="styleType === 'polygon'">
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="localStyle.fillColor"
                label="Füllfarbe"
                readonly
                @click="
                  showPolygonFillColorPicker = !showPolygonFillColorPicker
                "
                hide-details
              >
                <template v-slot:prepend-inner>
                  <v-avatar
                    size="24"
                    :style="`background-color: ${localStyle.fillColor}`"
                  ></v-avatar>
                </template>
              </v-text-field>
              <v-menu
                v-model="showPolygonFillColorPicker"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ props }">
                  <div v-bind="props"></div>
                </template>
                <v-card>
                  <v-color-picker
                    v-model="localStyle.fillColor"
                    mode="hex"
                  ></v-color-picker>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn variant="text" @click="showPolygonFillColorPicker = false">Close</v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="localStyle.color"
                label="Umrandungsfarbe"
                readonly
                @click="
                  showPolygonOutlineColorPicker = !showPolygonOutlineColorPicker
                "
                hide-details
              >
                <template v-slot:prepend-inner>
                  <v-avatar
                    size="24"
                    :style="`background-color: ${localStyle.color}`"
                  ></v-avatar>
                </template>
              </v-text-field>
              <v-menu
                v-model="showPolygonOutlineColorPicker"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ props }">
                  <div v-bind="props"></div>
                </template>
                <v-card>
                  <v-color-picker
                    v-model="localStyle.color"
                    mode="hex"
                  ></v-color-picker>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn variant="text" @click="showPolygonOutlineColorPicker = false">Close</v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <v-slider
                v-model="localStyle.weight"
                label="Umrandungsdicke"
                min="0"
                max="5"
                step="1"
                thumb-label
                hide-details
              ></v-slider>
            </v-col>
            <v-col cols="12" sm="6">
              <v-slider
                v-model="localStyle.fillOpacity"
                label="Fülldeckkraft"
                min="0"
                max="1"
                step="0.1"
                thumb-label
                hide-details
              ></v-slider>
            </v-col>
          </v-row>
        </div>

        <!-- Common style options for all geometry types -->
        <v-row>
          <v-col cols="12">
            <v-slider
              v-model="localStyle.opacity"
              label="Deckkraft"
              min="0"
              max="1"
              step="0.1"
              thumb-label
              hide-details
            ></v-slider>
          </v-col>
        </v-row>

        <!-- Style preview -->
        <v-row class="mt-4">
          <v-col cols="12">
            <v-card outlined>
              <v-card-subtitle>Stilvorschau</v-card-subtitle>
              <v-card-text
                class="d-flex justify-center align-center"
                style="height: 100px"
              >
                <!-- Preview for custom icon -->
                <img
                  v-if="
                    styleType === 'point' && hasCustomIcon && !iconLoadError
                  "
                  :src="getIconUrl()"
                  :style="`width: ${localStyle.radius}px; height: ${localStyle.radius}px; opacity: ${localStyle.opacity}`"
                  alt="Benutzerdefiniertes Icon Vorschau"
                  @error="handleIconError"
                />
                <!-- Fallback if icon fails to load -->
                <div
                  v-else-if="
                    styleType === 'point' && hasCustomIcon && iconLoadError
                  "
                  class="d-flex flex-column align-center"
                >
                  <v-icon color="grey">mdi-image-broken</v-icon>
                  <span class="text-caption grey--text"
                    >Icon preview unavailable</span
                  >
                </div>
                <!-- Preview for default circle marker -->
                <div
                  v-else-if="styleType === 'point'"
                  :style="`width: ${localStyle.radius * 2}px; height: ${localStyle.radius * 2}px; border-radius: 50%;
                              background-color: ${localStyle.color}; border: ${localStyle.weight}px solid ${localStyle.outlineColor || '#ffffff'};
                              opacity: ${localStyle.opacity}`"
                ></div>
                <div
                  v-else-if="styleType === 'line'"
                  :style="`width: 150px; height: ${localStyle.weight}px; 
                              background-color: ${localStyle.color}; opacity: ${localStyle.opacity};
                              ${localStyle.dashArray ? 'border-top-style: dashed;' : ''}`"
                ></div>
                <div
                  v-else-if="styleType === 'polygon'"
                  :style="`width: 150px; height: 100px; 
                              background-color: ${localStyle.fillColor}; opacity: ${localStyle.fillOpacity};
                              border: ${localStyle.weight}px solid ${localStyle.color};`"
                ></div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="applyStyle">Style anwenden</v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed } from "vue";
import {
  GeoPackageStyle,
  StyleType,
  getDefaultStyle,
} from "@/utils/geopackage-style-utils";

export default defineComponent({
  name: "GeoPackageStyleEditor",

  props: {
    style: {
      type: Object as PropType<GeoPackageStyle>,
      required: true,
    },
    geometryType: {
      type: String,
      required: true,
    },
    layerId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      localStyle: {
        color: "#3388ff",
        fillColor: "#3388ff",
        weight: 2,
        opacity: 0.8,
        fillOpacity: 0.6,
        radius: 8,
        outlineColor: "#ffffff",
        dashArray: "",
        icon: undefined,
      } as GeoPackageStyle & { outlineColor?: string },
      colorOptions: [
        { title: "Blue", value: "#3388ff" },
        { title: "Red", value: "#e31a1c" },
        { title: "Green", value: "#33a02c" },
        { title: "Yellow", value: "#ffff33" },
        { title: "Purple", value: "#6a3d9a" },
        { title: "Orange", value: "#ff7f00" },
        { title: "Gray", value: "#666666" },
        { title: "Black", value: "#000000" },
        { title: "White", value: "#ffffff" },
      ],
      lineStyleOptions: [
        { text: "Solid", value: "" },
        { text: "Dashed", value: "5,5" },
        { text: "Dotted", value: "2,5" },
        { text: "Dash-Dot", value: "10,5,2,5" },
      ],
      showFillColorPicker: false,
      showOutlineColorPicker: false,
      showLineColorPicker: false,
      showPolygonFillColorPicker: false,
      showPolygonOutlineColorPicker: false,
      iconLoadError: false,
    };
  },

  created() {
    // Initialize localStyle from the style prop on component creation
    this.initializeLocalStyle();
  },

  computed: {
    styleType(): StyleType {
      const lowerGeom = this.geometryType.toLowerCase();
      if (lowerGeom.includes("point")) {
        return StyleType.Point;
      } else if (lowerGeom.includes("line")) {
        return StyleType.Line;
      } else if (lowerGeom.includes("polygon")) {
        return StyleType.Polygon;
      }
      return StyleType.Point; // Default
    },
    hasCustomIcon(): boolean {
      // Check if the style has a custom icon (icon property exists and is not empty)
      return !!this.localStyle.icon;
    },
  },

  watch: {
    style: {
      handler(newStyle, oldStyle) {
        console.log("[GeoPackageStyleEditor] Style watcher triggered");
        console.log("[GeoPackageStyleEditor] newStyle:", newStyle);
        console.log("[GeoPackageStyleEditor] oldStyle:", oldStyle);

        // Determine the actual geometry type - prefer style.type if geometryType prop is Unknown
        let actualGeometryType = this.geometryType;
        if (this.geometryType === 'Unknown' && newStyle.type) {
          // Map style type to geometry type
          if (newStyle.type === 'line' || newStyle.type === 'multi_line') {
            actualGeometryType = 'LineString';
          } else if (newStyle.type === 'polygon' || newStyle.type === 'multi_polygon') {
            actualGeometryType = 'Polygon';
          } else if (newStyle.type === 'point' || newStyle.type === 'multi_point') {
            actualGeometryType = 'Point';
          }
        }

        console.log("[GeoPackageStyleEditor] actualGeometryType:", actualGeometryType);

        // Merge the new style with default style for this geometry type
        const defaultStyle = getDefaultStyle(actualGeometryType);
        console.log("[GeoPackageStyleEditor] defaultStyle:", defaultStyle);

        // Create a new local style object
        const mergedStyle = { ...defaultStyle, ...newStyle };

        // For custom icons, ensure radius is within acceptable range (8-64)
        if (newStyle.icon && mergedStyle.radius) {
          // If radius is too small (e.g., from QML millimeter values), scale it up
          if (mergedStyle.radius < 8) {
            mergedStyle.radius = Math.max(8, mergedStyle.radius * 8); // Scale by 8x if too small
          }
          // Clamp to valid range
          mergedStyle.radius = Math.min(64, Math.max(8, mergedStyle.radius));
          console.log(
            "[GeoPackageStyleEditor] Adjusted radius for custom icon:",
            mergedStyle.radius,
          );
        }

        // For point styles, map Leaflet's color (outline) to outlineColor for the UI
        // and fillColor to color (main color)
        if (
          actualGeometryType.toLowerCase().includes("point") &&
          !newStyle.icon
        ) {
          (mergedStyle as any).outlineColor =
            newStyle.color || defaultStyle.color || "#ffffff";
          mergedStyle.color =
            newStyle.fillColor ||
            newStyle.color ||
            defaultStyle.color ||
            "#3388ff";
        }

        // For line styles, if fillColor is set, use it as the main color
        // (because our map utils stores line color in fillColor for consistency)
        if (actualGeometryType.toLowerCase().includes("line")) {
          if (newStyle.fillColor) {
            mergedStyle.color = newStyle.fillColor;
          }
        }

        // Completely replace localStyle to ensure all properties are updated
        // Use Vue.set or direct assignment to trigger reactivity properly
        this.localStyle = { ...mergedStyle };

        // Reset icon load error when style changes
        this.iconLoadError = false;
      },
      deep: false, // Only trigger when the style prop reference changes, not when properties change
    },
  },

  methods: {
    initializeLocalStyle() {
      const newStyle = this.style;

      // Determine the actual geometry type - prefer style.type if geometryType prop is Unknown
      let actualGeometryType = this.geometryType;
      if (this.geometryType === 'Unknown' && newStyle.type) {
        // Map style type to geometry type
        if (newStyle.type === 'line' || newStyle.type === 'multi_line') {
          actualGeometryType = 'LineString';
        } else if (newStyle.type === 'polygon' || newStyle.type === 'multi_polygon') {
          actualGeometryType = 'Polygon';
        } else if (newStyle.type === 'point' || newStyle.type === 'multi_point') {
          actualGeometryType = 'Point';
        }
      }

      // Merge the new style with default style for this geometry type
      const defaultStyle = getDefaultStyle(actualGeometryType);
      const mergedStyle = { ...defaultStyle, ...newStyle };

      // For custom icons, ensure radius is within acceptable range (8-64)
      if (newStyle.icon && mergedStyle.radius) {
        if (mergedStyle.radius < 8) {
          mergedStyle.radius = Math.max(8, mergedStyle.radius * 8);
        }
        mergedStyle.radius = Math.min(64, Math.max(8, mergedStyle.radius));
      }

      // For point styles, map Leaflet's color (outline) to outlineColor for the UI
      if (actualGeometryType.toLowerCase().includes("point") && !newStyle.icon) {
        (mergedStyle as any).outlineColor = newStyle.color || defaultStyle.color || "#ffffff";
        mergedStyle.color = newStyle.fillColor || newStyle.color || defaultStyle.color || "#3388ff";
      }

      // For line styles, if fillColor is set, use it as the main color
      if (actualGeometryType.toLowerCase().includes("line")) {
        if (newStyle.fillColor) {
          mergedStyle.color = newStyle.fillColor;
        }
      }

      this.localStyle = { ...mergedStyle };
      this.iconLoadError = false;
    },

    applyStyle() {
      // Add the correct style type based on geometry
      this.localStyle.type = this.styleType;

      // Create a clean style object for emission
      const emitStyle: GeoPackageStyle = { ...this.localStyle };

      // For point styles, map outlineColor to color (Leaflet's outline property)
      // and the main color to fillColor
      if (this.styleType === StyleType.Point && !this.hasCustomIcon) {
        emitStyle.fillColor = this.localStyle.color;
        emitStyle.color = (this.localStyle as any).outlineColor || "#ffffff";
        // Keep fillOpacity same as opacity for now
        emitStyle.fillOpacity = this.localStyle.opacity;
      }

      // Tell parent to update the style
      this.$emit("update:style", emitStyle);
    },
    getIconUrl(): string {
      console.log("[GeoPackageStyleEditor] getIconUrl() called");
      console.log(
        "[GeoPackageStyleEditor] this.localStyle.icon:",
        this.localStyle.icon,
      );

      // Return the icon URL for the preview
      if (this.localStyle.icon) {
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "/jmapi/v1";
        console.log("[GeoPackageStyleEditor] apiBaseUrl:", apiBaseUrl);

        // Check if it's an icon ID (starts with "icon_") or a file path
        if (this.localStyle.icon.startsWith("icon_")) {
          // Database-stored icon
          const url = `${apiBaseUrl}/geopackage/icon/${this.localStyle.icon}`;
          console.log("[GeoPackageStyleEditor] Database icon URL:", url);
          return url;
        } else {
          // Filesystem icon
          const url = `${apiBaseUrl}/geopackage/icon-file?icon_path=${encodeURIComponent(this.localStyle.icon)}`;
          console.log("[GeoPackageStyleEditor] Filesystem icon URL:", url);
          return url;
        }
      }
      console.log(
        "[GeoPackageStyleEditor] No icon found, returning empty string",
      );
      return "";
    },
    handleIconError(event: Event) {
      // Handle icon load errors gracefully
      console.error("[GeoPackageStyleEditor] Failed to load icon preview");
      console.error(
        "[GeoPackageStyleEditor] Icon value:",
        this.localStyle.icon,
      );
      console.error(
        "[GeoPackageStyleEditor] Generated URL:",
        this.getIconUrl(),
      );
      console.error("[GeoPackageStyleEditor] Error event:", event);
      this.iconLoadError = true;
    },
  },
});
</script>

<style scoped>
.style-editor {
  padding: 8px;
}
</style>
